import React, { useEffect, useState } from "react";
import { Smartphone, X, Download } from "lucide-react";

/**
 * PWA install prompt — non-blocking bottom-sheet that nudges mobile users
 * to "Add to Home Screen" the first time they visit on iOS / Android.
 * Honors a 14-day dismissal cooldown via localStorage.
 *
 * Android/Chrome: uses beforeinstallprompt event (native).
 * iOS Safari: no API, so we render manual instructions only when ua matches.
 */
const DISMISS_KEY = "pb_pwa_dismissed_at";
const COOLDOWN_DAYS = 14;

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
}

function isIOSSafari() {
  const ua = window.navigator.userAgent || "";
  const ios = /iPad|iPhone|iPod/.test(ua);
  const safari = /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
  return ios && safari;
}

function recentlyDismissed() {
  const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
  if (!ts) return false;
  return (Date.now() - ts) < COOLDOWN_DAYS * 86_400_000;
}

export default function PWAInstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [show, setShow] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (recentlyDismissed()) return;

    const onBefore = (e) => {
      e.preventDefault();
      setDeferredEvent(e);
      // Wait 8s before nudging so first impression isn't broken.
      setTimeout(() => setShow(true), 8000);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS Safari fallback path.
    if (isIOSSafari()) {
      const t = setTimeout(() => { setIosMode(true); setShow(true); }, 12_000);
      return () => { window.removeEventListener("beforeinstallprompt", onBefore); clearTimeout(t); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  if (!show) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (deferredEvent) {
      deferredEvent.prompt();
      const { outcome } = await deferredEvent.userChoice;
      if (outcome === "accepted") { setShow(false); }
      else dismiss();
    }
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[min(92vw,400px)] card-pb p-4 shadow-2xl border-l-4 border-l-[#C9A356] animate-slide-up"
      role="dialog" aria-labelledby="pwa-title" data-testid="pwa-prompt"
    >
      <button onClick={dismiss} aria-label="Dismiss install prompt" className="absolute top-2 right-2 text-[#94A3B8] hover:text-[#0A2540]" data-testid="pwa-dismiss">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#C9A356]/15 text-[#C9A356] grid place-items-center shrink-0">
          <Smartphone className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 id="pwa-title" className="font-heading text-sm font-semibold text-[#0A2540] dark:text-white">Install PowerBuilt</h3>
          {iosMode ? (
            <p className="text-xs text-[#475569] dark:text-slate-400 mt-1">
              Tap the <strong>Share</strong> icon, then <strong>Add to Home Screen</strong> for one-tap access offline.
            </p>
          ) : (
            <p className="text-xs text-[#475569] dark:text-slate-400 mt-1">Get one-tap access from your home screen and full offline support.</p>
          )}
          {!iosMode && (
            <button onClick={install} className="btn-accent text-xs mt-2.5" data-testid="pwa-install-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Install
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
